import { Info } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { isNil } from "lodash";
import {
  ChangeEvent,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useApi, useDownload } from "../hooks/useApi";
import { TimeSheet } from "../types/timesheet";
import TimesheetExpandedContent from "./TimesheetExpandedContent";
import TimesheetFilters from "./TimesheetFilters";

interface Props {
  type: "outstanding" | "recently approved" | "history";
  onTimeSheetAmend?: (timesheet: TimeSheet) => void;
  setAlertOpen?: Dispatch<SetStateAction<boolean>>;
  setAlertMessage?: Dispatch<SetStateAction<string>>;
  onDataLoaded?: (total: number) => void;
}

export const MobileTimesheetAccordion: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const {
    type,
    onTimeSheetAmend,
    setAlertMessage,
    setAlertOpen,
    onDataLoaded,
  } = props;
  const rowsPerPage = 7;
  const [page, setPage] = useState<number>(0);
  // const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [approvedRow, setApprovedRow] = useState<TimeSheet | null>(null);
  const [searchFilter, setSearchFilter] = useState<
    {
      fieldName:
        | "candidate"
        | "bookingContact"
        | "jobTitle"
        | "jobDescription"
        | "location"
        | "client"
        | "weekEnding";
      value: string;
    }[]
  >();
  const { data: timesheets, isLoading } = useApi<TimeSheet[]>(
    "timesheets",
    {
      status:
        type === "outstanding"
          ? [0, 2, 3]
          : type === "recently approved"
          ? [1]
          : [4],
      search: searchFilter ? JSON.stringify(searchFilter) : "",
    },
    {
      page: page + 1,
      count: rowsPerPage,
    }
  );

  const count = timesheets?.paging?.totalCount ?? 0;
  const pages = timesheets?.paging?.totalPages;

  const { t } = useTranslation();
  const { download } = useDownload();

  const handleApprove = (timesheet: TimeSheet) => {
    setApprovedRow(timesheet);
    setAlertOpen!(true);
    setAlertMessage!(t("alerts.approveTimesheet"));
  };

  const handleChangePage = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage - 1);
  };

  useEffect(() => {
    if (!isLoading && !isNil(timesheets?.paging?.totalCount)) {
      if (onDataLoaded) onDataLoaded(timesheets?.paging?.totalCount);
    }
  }, [isLoading, onDataLoaded, timesheets?.paging?.totalCount]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <TimesheetFilters
        type={type}
        isEnabled={
          timesheets?.results && timesheets.results?.length > 0 ? true : false
        }
        onApplyFilters={(filters) => {
          const searchObj: {
            fieldName:
              | "candidate"
              | "bookingContact"
              | "jobTitle"
              | "jobDescription"
              | "location"
              | "client"
              | "weekEnding";
            value: string;
          }[] = [];
          if (filters.candidate) {
            searchObj.push({
              fieldName: "candidate",
              value: filters.candidate,
            });
          }
          if (filters.bookingContact) {
            searchObj.push({
              fieldName: "bookingContact",
              value: filters.bookingContact,
            });
          }
          if (filters.jobTitle) {
            searchObj.push({
              fieldName: "jobTitle",
              value: filters.jobTitle,
            });
          }
          if (filters.jobDescription) {
            searchObj.push({
              fieldName: "jobDescription",
              value: filters.jobDescription,
            });
          }
          if (filters.location) {
            searchObj.push({
              fieldName: "location",
              value: filters.location,
            });
          }
          if (filters.client) {
            searchObj.push({
              fieldName: "client",
              value: filters.client,
            });
          }
          if (filters.weekEnding) {
            searchObj.push({
              fieldName: "weekEnding",
              value: filters.weekEnding,
            });
          }
          setSearchFilter(searchObj);
        }}
      />
      {!isLoading ? (
        <>
          {/* {alertOpen && (
            <Alert severity="success" onClose={handleAlertClose}>
              Thank you for approving this timesheet.
            </Alert>
          )} */}
          {isSmallScreen && (
            <>
              {type === "outstanding" && (
                <Box sx={{ pb: "20px" }}>
                  <Chip
                    label={`${count} ${t("current.outstanding2")}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>
              )}

              <Typography variant="body2" sx={{ pb: "20px" }}>
                {" "}
                * {t("timesheets.weekEndingText")}
              </Typography>
            </>
          )}

          {timesheets?.results?.map((timesheet: TimeSheet) => (
            <Accordion key={timesheet.id}>
              <AccordionSummary
                expandIcon={<KeyboardArrowDownIcon />}
                sx={{
                  backgroundColor:
                    timesheet === approvedRow
                      ? "rgba(76, 175, 80, 0.1)"
                      : timesheet.status === 3
                      ? "rgba(239, 108, 0, 0.1)"
                      : "white",
                }}
              >
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  spacing={1}
                  sx={{ width: "90%" }}
                >
                  <Stack direction={"row"} spacing={1}>
                    {timesheet.isAmended && timesheet.status === 4 && (
                      <Stack
                        direction={"row"}
                        paddingRight={0}
                        alignItems={"center"}
                        justifyItems={"center"}
                      >
                        <Tooltip title={t("tooltips.isAmended")} arrow>
                          <Info fontSize="small" color="primary" />
                        </Tooltip>
                      </Stack>
                    )}
                    <Stack>
                      <Typography variant="body1">
                        {" "}
                        {timesheet.candidate}{" "}
                      </Typography>
                      <Typography variant="caption">
                        {" "}
                        {t("tsTable.weekEnding")}* -{" "}
                        {format(parseISO(timesheet.weekEnding), "dd/MM/yyyy")}{" "}
                      </Typography>
                    </Stack>
                  </Stack>
                  {timesheet.status === 4 && (
                    <Tooltip title={t("tooltips.download")}>
                      <IconButton
                        aria-label="download"
                        onClick={() =>
                          download(timesheet.id, timesheet.weekEnding)
                        }
                        sx={{ p: 0 }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: "#F6F6F6" }}>
                <TimesheetExpandedContent
                  timesheet={timesheet}
                  onApprove={handleApprove}
                  onAmendClick={(timesheet) => onTimeSheetAmend!(timesheet)}
                />
              </AccordionDetails>
            </Accordion>
          ))}

          {timesheets?.results && timesheets?.results?.length > 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: "20px" }}>
              <Pagination
                count={pages ?? 0}
                color="primary"
                page={count <= 0 ? 0 : page + 1}
                onChange={handleChangePage}
                size="small"
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography variant="body1">
                {" "}
                {t("adminTable.noResults")}{" "}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box paddingTop={4} display="flex" justifyContent="center">
          <CircularProgress size={"50px"} />
        </Box>
      )}
    </>
  );
};

export default MobileTimesheetAccordion;
